// built-from: /shared-process-spine v1 impl:shared-process-spine role:implementation
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ProcessSpineStep } from './useEngagementSetup';

export function sharedCommandId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID() : `process-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export interface SharedProcess {
  processId: string;
  name: string;
  revision: string;
  steps: Array<ProcessSpineStep & { statementId?: number; localId?: number; processId?: string }>;
  flowText?: string;
  domainFlowText?: string;
  agreementCurrent?: boolean;
  agreement?: { needsReview?: boolean } | null;
  rejected?: { raw: unknown; reason?: string } | null;
  reviewState?: string;
  doc?: { flowText: string; domainFlowText: string; rejected?: { raw: unknown } | null };
}

export function useSharedProcess(engagementId: string | null, processId: string | null) {
  const [process, setProcess] = useState<SharedProcess>();
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const focus = useRef('');
  const loadedContext = useRef('');
  const pending = useRef(new Map<string, string>());
  const key = `${engagementId}/${processId}`;
  focus.current = key;
  const path = engagementId && processId
    ? `/api/engagements/${encodeURIComponent(engagementId)}/processes/${encodeURIComponent(processId)}` : null;
  const refresh = useCallback(async () => {
    if (!path) return;
    try {
      const response = await fetch(`${path}/shared`);
      if (!response.ok) throw new Error('The shared process could not be loaded. Try again.');
      const value = await response.json() as SharedProcess;
      if (focus.current === key) { loadedContext.current = key; setProcess(value); setError(null); }
    } catch (failure) { if (focus.current === key) setError((failure as Error).message); }
  }, [path, key]);
  useEffect(() => { setProcess(undefined); setError(null); void refresh(); }, [refresh]);
  const command = useCallback(async (operation: Record<string, unknown>) => {
    if (!path || !process || loadedContext.current !== key) return false;
    setSaving(true);
    const signature = `${key}/${process.revision}/${JSON.stringify(operation)}`;
    const commandId = pending.current.get(signature) || sharedCommandId();
    pending.current.set(signature, commandId);
    try {
      const response = await fetch(`${path}/commands`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ expectedRevision: process.revision, commandId, operation }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error?.message || body.error || 'The change was not saved. Reload and try again.');
      pending.current.delete(signature);
      await refresh();
      window.dispatchEvent(new Event('shared-process-changed'));
      return true;
    } catch (failure) { if (focus.current === key) setError((failure as Error).message); return false; }
    finally { setSaving(false); }
  }, [key, path, process, refresh]);
  const focusSession = useCallback(async (sessionId: string, focusedProcessId = processId) => {
    if (!focusedProcessId) return;
    const response = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}/focus`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ processId: focusedProcessId }),
    });
    if (!response.ok) throw new Error('The activity could not be focused on this process. No conversation was opened.');
  }, [processId]);
  return { process: loadedContext.current === key ? process : undefined, error, saving, refresh, command, focusSession };
}
